import styles from './groups.module.scss';
import { useState } from 'react';
import { ValueFormatterParams } from 'ag-grid-community';
import { makeRequest, useGetRequestNewHook } from '../../../../requestUtils';
import { CreateHeaders, Dialog, DialogActions, DialogContent, DialogTitle, GrayOutlinedButton, HeaderSizeEnum, Icon, LoadingButton, MultiLineTextField, PrimaryButton, SecondaryButton, TextAreaAutoSize, TextField, textHeader, Typography } from '@frontend-workspace/react-shared-lib';
import ProductStructureGroupDetailsGridRenderer from '../../../product-structure-group-details-grid-renderer/product-structure-group-details-grid-renderer';
import AddGroupDialog from '../../../add-group-dialog/add-group-dialog';
import { AddProductsDialog } from '../../../add-products-dialog/add-products-dialog';
import DeleteProductCategoryDialog from '../../../delete-product-category-dialog/delete-product-category-dialog';
import { IRelationProductGroups } from '../../../../_models/relation-product-groups';
import { WebkuaTable } from '@frontend-workspace/webkua-lib';
import { useTranslation } from 'react-i18next';

/* eslint-disable-next-line */
export interface GroupsProps { }

export function Groups(props: GroupsProps) {
  const { t } = useTranslation();
  const { data: relationProductGroups, getData, isLoading }: { data: IRelationProductGroups[], getData: () => void, isLoading: boolean } = useGetRequestNewHook(`relationProductGroups`, []);
  const [addGroup, setAddGroup] = useState<boolean>(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string | undefined>(undefined);
  const [deleteProductCategory, setDeleteProductCategory] = useState<IRelationProductGroups | undefined>(undefined);
  const [editDescriptionGroupId, setEditDescriptionGroupId] = useState<string | undefined>(undefined);
  const [editGroupId, setEditPukGroupId] = useState<string | undefined>(undefined);

  const numberOfProductsRenderer = (params: ValueFormatterParams) => params.data.mainProducts?.length;

  const openAddGroupDialog = () => setAddGroup(true);
  const closeAddGroupDialog = () => {
    setAddGroup(false);
    getData();
  };

  const openDeleteProductCategoryDialog = (e: any) => setDeleteProductCategory(e.data);
  const closeDeleteProductCategoryDialog = () => setDeleteProductCategory(undefined);

  const closeAddProductsDialog = () => {
    setSelectedGroupId(undefined);
    getData();
  };

  const addProductRenderer = (params: ValueFormatterParams) => {
    const onClick = () => setSelectedGroupId(params.data.id);
    return (
      <PrimaryButton
        variant="outlined"
        size='small'
        onClick={onClick}>
        {t("standard.addProduct")}
      </PrimaryButton>
    );
  };

  const deleteProductCategoryRenderer = (params: ValueFormatterParams) => (
    <SecondaryButton
      variant="outlined"
      size='small'
      onClick={() => openDeleteProductCategoryDialog(params)}>
      {t("groups.deleteProductGroup")}
    </SecondaryButton>
  );
  const editDescriptionRenderer = (params: ValueFormatterParams) => (
    <SecondaryButton
      variant="outlined"
      size='small'
      onClick={() => setEditDescriptionGroupId(params.value)}>
      {t("groups.editDescription")}
    </SecondaryButton>
  );
  const editPukRenderer = (params: ValueFormatterParams) => (
    <SecondaryButton
      variant="outlined"
      size='small'
      onClick={() => setEditPukGroupId(params.value)}>
      {t("standard.change")}
    </SecondaryButton>
  );

  const headers = CreateHeaders([
    textHeader("groupName", t("groups.productCategories"), HeaderSizeEnum.EXTRA_LARGE, {
      renderer: 'agGroupCellRenderer',
      cellStyle: { fontSize: '0.875rem', fontWeight: '600' }
    }),
    textHeader("description", t("groups.description"), HeaderSizeEnum.LARGE),
    textHeader("", t("standard.products"), HeaderSizeEnum.SMALL, {
      renderer: numberOfProductsRenderer,
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    textHeader("subscriptionRenewalPUK", t("groups.renewalPUK"), HeaderSizeEnum.SMALL),
    textHeader("maintenancePUK", t("groups.maintenancePUK"), HeaderSizeEnum.SMALL),
    textHeader("", "", HeaderSizeEnum.SMALL, {
      renderer: addProductRenderer,
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    textHeader("id", "", HeaderSizeEnum.SMALL, {
      renderer: editPukRenderer,
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    textHeader("", "", HeaderSizeEnum.MEDIUM, {
      renderer: deleteProductCategoryRenderer,
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    })
  ]);
  return <>
    <div className="d-flex flex-row align-items-end">
      <div className='mb-3 mb-lg-0 me-4'>
        <PrimaryButton
          variant="contained"
          onClick={() => openAddGroupDialog()}>
          <Icon className="me-1" fontSize="small">add</Icon>{t("standard.addProductGroup")}
        </PrimaryButton>
      </div>
    </div>
    <WebkuaTable
      isLoading={isLoading}
      headers={headers}
      expandComponent={ProductStructureGroupDetailsGridRenderer}
      data={relationProductGroups}
    />
    <AddGroupDialog
      open={!!addGroup}
      onClose={closeAddGroupDialog}
    />
    {selectedGroupId && <AddProductsDialog
      open={!!selectedGroupId}
      onClose={closeAddProductsDialog}
      groupId={selectedGroupId}
    />}
    {deleteProductCategory && <DeleteProductCategoryDialog
      open={!!deleteProductCategory}
      onClose={closeDeleteProductCategoryDialog}
      groupName={deleteProductCategory.groupName}
      groupId={deleteProductCategory.id}
    />}
    {editGroupId && <EditGroupDialog
      onClose={() => setEditPukGroupId(undefined)}
      onRefresh={getData}
      group={relationProductGroups.find(e => e.id === editGroupId)}
    />}
  </>;
}

function EditGroupDialog(props: { onClose: () => void; onRefresh: () => void, group?: IRelationProductGroups; }) {
  const { t } = useTranslation();
  if (!props.group) return null;
  const [renewalPUK, setRenewalPUK] = useState<string | undefined>(props.group.subscriptionRenewalPUK);
  const [maintenancePUK, setMaintenancePUK] = useState<string | undefined>(props.group.maintenancePUK);
  const [description, setDescription] = useState<string | undefined>(props.group.description);

  const handleUpdate = () => {
    makeRequest(`relationProductGroups/`, 'PUT', {
      renewalPUK: renewalPUK,
      maintenanceRenewalPUK: maintenancePUK,
      description: description,
      groupId: props.group?.id
    }).then(response => {
      props.onRefresh();
    });
    props.onClose();
  };
  return (
    <Dialog
      open
      maxWidth="md"
      fullWidth>
      <DialogTitle className='mb-4'>
        {t("groups.changeProductGroupDescription")}
      </DialogTitle>
      <DialogContent>
        <Typography
          component="div"
          variant='subtitle3'
          className='mb-3'>
          {t("groups.productCategory")}: {props.group.groupName}
        </Typography>
        <MultiLineTextField
          label={t("standard.addDescription")}
          name='description'
          fullWidth
          minRows={1}
          value={description || ''}
          onChange={(e: any) => setDescription(e.target.value)}
        />
        <MultiLineTextField
          label={t("groups.renewalPUK")}
          name='description'
          fullWidth
          minRows={1}
          value={renewalPUK || ''}
          onChange={(e: any) => setRenewalPUK(e.target.value)}
        />
        <MultiLineTextField
          label={t("groups.maintenancePUK")}
          name='description'
          fullWidth
          minRows={1}
          value={maintenancePUK || ''}
          onChange={(e: any) => setMaintenancePUK(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <PrimaryButton
          variant="contained"
          size='large'
          onClick={handleUpdate}
          className='minButtonWidth'>
          {t("standard.save")}
        </PrimaryButton>
        <GrayOutlinedButton
          variant="outlined"
          size='large'
          onClick={props.onClose}
          className='minButtonWidth'>
          {t("standard.cancel")}
        </GrayOutlinedButton>
      </DialogActions>
    </Dialog>
  );
}

export default Groups;